import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Eye, Search, Filter, ChevronLeft, ChevronRight, User } from 'lucide-react';
import AdminNavbar from '../components/AdminNavbar';
import api from '../services/api';
import toast from 'react-hot-toast';

const AdminKYCPage = () => {
    const [kycRequests, setKycRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedKYC, setSelectedKYC] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    useEffect(() => {
        fetchKYCRequests();
    }, [page]);

    const fetchKYCRequests = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/admin/kyc/pending?limit=10&offset=${(page - 1) * 10}`);
            setKycRequests(res.data.kycSubmissions);
            setTotalPages(Math.ceil(res.data.pagination.total / 10));
        } catch (error) {
            toast.error('Failed to load KYC requests');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (kyc) => {
        setSelectedKYC(kyc);
        setShowModal(true);
        setRejectionReason('');
    };

    const handleReview = async (status) => {
        try {
            if (status === 'rejected' && !rejectionReason) {
                return toast.error('Please provide a reason for rejection');
            }

            await api.put(`/admin/kyc/${selectedKYC.id}/review`, {
                status,
                rejectionReason: status === 'rejected' ? rejectionReason : null
            });

            toast.success(`KYC ${status} successfully`);
            setShowModal(false);
            fetchKYCRequests(); // Refresh list
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to review KYC');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const getImageUrl = (path) => {
        console.log('🖼️ getImageUrl called with path:', path);
        if (!path) {
            console.log('⚠️ No path provided');
            return '';
        }
        // If path is full URL, return it
        if (path.startsWith('http')) {
            console.log('✅ Full URL detected:', path);
            return path;
        }
        // Database stores path like: uploads/kyc/1234567890-123456789.png
        // Just normalize slashes and prepend base URL
        const normalizedPath = path.replace(/\\/g, '/');
        const fullUrl = `${process.env.REACT_APP_API_URL.replace('/api', '')}/${normalizedPath}`;
        console.log('🔗 Generated URL:', fullUrl);
        return fullUrl;
    };

    return (
        <div className="bg-[#181a20] min-h-screen">
            <AdminNavbar />
            <div className="pt-8 pb-12 px-4 sm:px-6 lg:px-8 max-w-[1600px] mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-white mb-2">KYC Verification Requests</h1>
                        <p className="text-gray-400">Review and approve user identity documents</p>
                    </div>
                </div>

                {/* List Table */}
                <div className="bg-[#1e2329] rounded-xl border border-gray-800 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-gray-800 bg-[#2b3139]/50">
                                    <th className="px-6 py-4 text-xs font-medium text-gray-400 uppercase">User</th>
                                    <th className="px-6 py-4 text-xs font-medium text-gray-400 uppercase">Country</th>
                                    <th className="px-6 py-4 text-xs font-medium text-gray-400 uppercase">Doc Type</th>
                                    <th className="px-6 py-4 text-xs font-medium text-gray-400 uppercase">Submitted</th>
                                    <th className="px-6 py-4 text-xs font-medium text-gray-400 uppercase text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {loading ? (
                                    <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">Loading requests...</td></tr>
                                ) : kycRequests.length === 0 ? (
                                    <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">No pending KYC requests</td></tr>
                                ) : (
                                    kycRequests.map((kyc) => (
                                        <tr key={kyc.id} className="hover:bg-[#2b3139]/20 transition">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center text-gray-300">
                                                        <User size={14} />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-white">{kyc.user?.firstName} {kyc.user?.lastName}</div>
                                                        <div className="text-xs text-gray-500">{kyc.user?.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-300">{kyc.country}</td>
                                            <td className="px-6 py-4 text-sm text-gray-300 uppercase">{kyc.documentType.replace('_', ' ')}</td>
                                            <td className="px-6 py-4 text-sm text-gray-400">{formatDate(kyc.createdAt)}</td>
                                            <td className="px-6 py-4 text-right">
                                                <button onClick={() => handleViewDetails(kyc)} className="text-[#fcd535] hover:text-[#f0b90b] text-sm font-medium">
                                                    Review
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-between items-center px-6 py-4 border-t border-gray-800">
                            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
                            <div className="flex gap-2">
                                <button disabled={page === 1} onClick={() => setPage(page - 1)} className="p-2 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white"><ChevronLeft size={16} /></button>
                                <button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="p-2 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white"><ChevronRight size={16} /></button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Review Modal */}
                {showModal && selectedKYC && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                        <div className="bg-[#1e2329] border border-gray-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-[#1e2329] border-b border-gray-800 p-6 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-white">Review KYC Application</h2>
                                <button onClick={() => setShowModal(false)}><XCircle className="text-gray-400 hover:text-white" /></button>
                            </div>

                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Details */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-[#fcd535]">User Details</h3>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div className="text-gray-400">Name:</div><div className="text-white">{selectedKYC.user?.firstName} {selectedKYC.user?.lastName}</div>
                                        <div className="text-gray-400">Email:</div><div className="text-white">{selectedKYC.user?.email}</div>
                                        <div className="text-gray-400">Address:</div><div className="text-white">{selectedKYC.address}, {selectedKYC.city}</div>
                                        <div className="text-gray-400">Country:</div><div className="text-white">{selectedKYC.country}</div>
                                        <div className="text-gray-400">Doc Type:</div><div className="text-white uppercase">{selectedKYC.documentType.replace('_', ' ')}</div>
                                        <div className="text-gray-400">Doc Number:</div><div className="text-white">{selectedKYC.documentNumber}</div>
                                    </div>

                                    <div className="pt-4 border-t border-gray-800">
                                        <h3 className="text-lg font-medium text-[#fcd535] mb-4">Action</h3>
                                        <div className="flex gap-4 mb-4">
                                            <button onClick={() => handleReview('approved')} className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium">Approve</button>
                                            <button onClick={() => handleReview('rejected')} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium">Reject</button>
                                        </div>
                                        <textarea
                                            placeholder="Reason for rejection (if rejecting)..."
                                            className="w-full bg-[#2b3139] border border-gray-700 rounded-lg p-3 text-white text-sm"
                                            rows="3"
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                        ></textarea>
                                    </div>
                                </div>

                                {/* Images */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-medium text-[#fcd535]">Documents</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-gray-400 text-sm mb-1">Front of ID</p>
                                            <img
                                                src={getImageUrl(selectedKYC.documentFrontImage)}
                                                alt="ID Front"
                                                className="w-full max-w-md h-auto rounded-lg border border-gray-700"
                                                onError={(e) => console.error('❌ Image failed to load:', e.target.src)}
                                            />
                                            <p className="text-xs text-yellow-400 mt-1 break-all">URL: {getImageUrl(selectedKYC.documentFrontImage)}</p>
                                        </div>
                                        {selectedKYC.documentBackImage && (
                                            <div>
                                                <p className="text-gray-400 text-sm mb-1">Back of ID</p>
                                                <img src={getImageUrl(selectedKYC.documentBackImage)} alt="ID Back" className="w-full max-w-md h-auto rounded-lg border border-gray-700" />
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-gray-400 text-sm mb-1">Selfie</p>
                                            <img src={getImageUrl(selectedKYC.selfieImage)} alt="Selfie" className="w-full max-w-md h-auto rounded-lg border border-gray-700" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminKYCPage;
